import { useMemo, useState } from 'react';
import {
  UsersRound,
  Plus,
  Trash2,
  Pencil,
  UserPlus,
  AlertTriangle,
  Mail,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Spinner,
  useToast,
} from '../../ui';
import { PeopleInput } from '../common/PeopleInput';
import { useTeams } from '../../features/teams/useTeams';
import { useRooms } from '../../features/rooms/useRooms';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { memberFromPerson } from '../../features/rooms/roomModel';
import {
  blankMember,
  cleanMembers,
  validateTeam,
} from '../../features/teams/teamModel';
import type {
  Team,
  TeamDraft,
  TeamMember,
  TeamSummary,
} from '../../features/teams/types';

interface DraftState {
  name: string;
  description: string;
  members: TeamMember[];
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  description: '',
  members: [blankMember()],
};

function draftFromTeam(team: Team | TeamSummary): DraftState {
  return {
    name: team.name,
    description: team.description ?? '',
    members: team.members.length ? [...team.members] : [blankMember()],
  };
}

/**
 * Teams — manage local team rosters (Name, Alias, Role, Email). These rosters
 * feed the app's people pickers via the shared people directory. Create a team,
 * add members, and the aliases become selectable across the app.
 *
 * Strict layering: this page reaches data ONLY through the useTeams hook.
 */
export function TeamsPage() {
  const { teams, people, loading, error, create, update, remove } = useTeams();
  const { addMembers: addRoomMembers } = useRooms();
  const { activeRoomId } = useActiveRoom();
  const { toast } = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Team | TeamSummary | undefined>(undefined);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TeamSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setEditing(undefined);
    setDraft(EMPTY_DRAFT);
    setProblems([]);
    setModalOpen(true);
  };

  const openEdit = (team: TeamSummary) => {
    setEditing(team);
    setDraft(draftFromTeam(team));
    setProblems([]);
    setModalOpen(true);
  };

  const setMember = (index: number, patch: Partial<TeamMember>) =>
    setDraft((d) => ({
      ...d,
      members: d.members.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    }));

  const addMember = () =>
    setDraft((d) => ({ ...d, members: [...d.members, blankMember()] }));

  const removeMember = (index: number) =>
    setDraft((d) => ({
      ...d,
      members: d.members.filter((_, i) => i !== index),
    }));

  const alreadyAdded = useMemo(
    () => draft.members.map((m) => m.alias).filter(Boolean),
    [draft.members],
  );

  const submit = async () => {
    const problemsFound = validateTeam({ name: draft.name, members: draft.members });
    if (problemsFound.length > 0) {
      setProblems(problemsFound);
      return;
    }
    setSaving(true);
    const payload: TeamDraft = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      members: cleanMembers(draft.members),
    };
    try {
      if (editing && editing.id) {
        await update(editing.id, payload);
        toast({ tone: 'success', title: 'Team updated', description: payload.name });
      } else {
        await create(payload);
        toast({ tone: 'success', title: 'Team created', description: payload.name });
      }

      // Membership-aware rooms: grant the team's members access to the active
      // room by mirroring them as room members (role "member"). addMembers skips
      // aliases already present and re-saves the room so its pointer's mirrored
      // memberAliases updates. Best-effort — a room-sync hiccup never blocks the
      // team save the user just confirmed.
      if (activeRoomId && payload.members.length > 0) {
        try {
          await addRoomMembers(
            activeRoomId,
            payload.members.map((m) => memberFromPerson(m)),
          );
        } catch {
          /* non-fatal: the team is saved; room membership can be re-synced later */
        }
      }

      setModalOpen(false);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not save team',
        description: e instanceof Error ? e.message : 'Save failed.',
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Team removed', description: pendingDelete.name });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove team',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<UsersRound className="h-5 w-5" />}
        title="Teams"
        description="Manage team rosters (name, alias, role, email). Members feed the people pickers across the app."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
            New team
          </Button>
        }
      />

      <div className="mb-4 text-xs text-zinc-500">
        {people.length} {people.length === 1 ? 'person' : 'people'} across{' '}
        {teams.length} {teams.length === 1 ? 'team' : 'teams'} in the directory.
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading teams" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load teams"
          description={error}
        />
      ) : teams.length === 0 ? (
        <EmptyState
          icon={<UsersRound className="h-6 w-6" />}
          title="No teams yet"
          description="Create your first team and add members. Their aliases will populate the people pickers used across the app."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
              New team
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {teams.map((team) => (
            <Card key={team.id}>
              <CardHeader>
                <CardTitle>{team.name}</CardTitle>
                <div className="flex items-center gap-1">
                  <Badge tone="neutral" dot>
                    {team.members.length}{' '}
                    {team.members.length === 1 ? 'member' : 'members'}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => openEdit(team)}
                    aria-label={`Edit ${team.name}`}
                    className="rounded-md p-1.5 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-zinc-200"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(team)}
                    aria-label={`Delete ${team.name}`}
                    className="rounded-md p-1.5 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </CardHeader>
              <CardBody className="space-y-3">
                {team.description && (
                  <p className="text-sm text-zinc-400">{team.description}</p>
                )}
                {team.members.length === 0 ? (
                  <p className="text-xs text-zinc-500">No members yet.</p>
                ) : (
                  <ul className="divide-y divide-surface-border">
                    {team.members.map((m) => (
                      <li
                        key={m.alias || m.name}
                        className="flex items-center justify-between gap-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm text-zinc-200">
                            {m.name || m.alias}
                            <span className="ml-1.5 text-xs text-zinc-500">
                              @{m.alias}
                            </span>
                          </p>
                          {(m.role || m.email) && (
                            <p className="flex items-center gap-2 truncate text-xs text-zinc-500">
                              {m.role && <span>{m.role}</span>}
                              {m.email && (
                                <a
                                  href={`mailto:${m.email}`}
                                  className="inline-flex items-center gap-1 hover:text-accent"
                                >
                                  <Mail className="h-3 w-3" /> {m.email}
                                </a>
                              )}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="xl"
        title={editing && editing.id ? 'Edit team' : 'New team'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={saving}>
              {editing && editing.id ? 'Save team' : 'Create team'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {problems.length > 0 && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-3.5 py-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-red-300">
                <AlertTriangle className="h-3.5 w-3.5" /> Please fix:
              </p>
              <ul className="list-inside list-disc text-xs text-red-200">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          <Input
            label="Team name"
            placeholder="e.g. Checkout Platform"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
          <Input
            label="Description (optional)"
            placeholder="What this team owns"
            value={draft.description}
            onChange={(e) =>
              setDraft((d) => ({ ...d, description: e.target.value }))
            }
          />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Members
              </p>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<UserPlus className="h-4 w-4" />}
                onClick={addMember}
              >
                Add member
              </Button>
            </div>
            <div className="space-y-3">
              {draft.members.map((m, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 items-end gap-2 rounded-xl border border-surface-border bg-surface-1 p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]"
                >
                  <Input
                    label="Name"
                    placeholder="Jane Smith"
                    value={m.name}
                    onChange={(e) => setMember(i, { name: e.target.value })}
                  />
                  <PeopleInput
                    label="Alias"
                    value={m.alias}
                    onChange={(alias) => setMember(i, { alias })}
                    people={people}
                    exclude={alreadyAdded.filter((a) => a !== m.alias)}
                  />
                  <Input
                    label="Role"
                    placeholder="SDE II"
                    value={m.role}
                    onChange={(e) => setMember(i, { role: e.target.value })}
                  />
                  <Input
                    label="Email"
                    placeholder="jsmith@amazon.com"
                    value={m.email}
                    onChange={(e) => setMember(i, { email: e.target.value })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove member ${i + 1}`}
                    onClick={() => removeMember(i)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {draft.members.length === 0 && (
                <p className="text-xs text-zinc-500">
                  No members. Add at least one.
                </p>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete team?"
        confirmLabel="Delete team"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message={
          <>
            Delete <span className="font-medium text-zinc-100">{pendingDelete?.name}</span>?
            This removes its roster from the people directory used across the app
            and can't be undone.
          </>
        }
      />
    </div>
  );
}

export default TeamsPage;
